import { useQuery } from '@tanstack/react-query';
import {
  type DocumentPair,
  type DocumentReference,
  type OfferDocumentModel,
  offerDocumentModel,
  type PreventivoDocumentModel,
  preventivoDocumentModel,
  type QuoteDocumentKind,
  quoteDocumentBlocked,
  quoteDocumentFileName,
  type QuoteResponse,
} from '@k-management/shared';
import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { apiRequest } from '../../../lib/api.js';
import { quoteKeys, useQuoteLookups } from '../quotes-ui.js';
import './documents.css';

/**
 * The documents of a quote in PDF (rule of 03/10/2026), printed by the browser
 * with «Salva come PDF»: the «Offerta economica» for the client and the
 * «Preventivo interno». The texts come from the shared models, the same ones
 * the API writes in the Word file. Opened from «Documenti» with `?stampa`, the
 * page asks the browser to print as soon as it is ready.
 */
export function QuoteDocumentPage() {
  const { id = '', kind = 'offerta' } = useParams();
  const [search] = useSearchParams();
  const quote = useQuery({
    queryKey: quoteKeys.quote(id),
    queryFn: () => apiRequest<QuoteResponse>(`/quotes/${id}`),
  });
  const lookups = useQuoteLookups();
  const data = quote.data;
  const documentKind: QuoteDocumentKind = kind === 'preventivo' ? 'preventivo' : 'offerta';
  const blocked = data ? quoteDocumentBlocked(data, documentKind) : null;
  const ready = Boolean(data && lookups.data && !blocked);

  // The name the PDF is saved with.
  useEffect(() => {
    if (!data) return;
    document.title = quoteDocumentFileName(data, documentKind);
    return () => {
      document.title = 'K-Management';
    };
  }, [data, documentKind]);

  const autoPrint = search.has('stampa');
  useEffect(() => {
    if (!ready || !autoPrint) return;
    let cancelled = false;
    // The fonts first, or the PDF comes out in the fallback font.
    void document.fonts.ready.then(() => {
      if (!cancelled) window.print();
    });
    return () => {
      cancelled = true;
    };
  }, [ready, autoPrint]);

  let content: ReactNode = null;
  if (quote.isPending || lookups.isPending) content = <p>Caricamento…</p>;
  else if (quote.isError) content = <p className="error">{quote.error.message}</p>;
  else if (blocked) content = <p className="notice info">{blocked}</p>;
  else if (data && lookups.data) {
    content =
      documentKind === 'offerta' ? (
        <OfferDocument model={offerDocumentModel(data, lookups.data)} />
      ) : (
        <PreventivoDocument model={preventivoDocumentModel(data, lookups.data, new Date())} />
      );
  }

  return (
    <div className="doc-screen">
      <div className="doc-toolbar">
        <Link to={`/preventivatore/${id}`}>← Torna al preventivo</Link>
        <span className="spacer" />
        <span className="muted small">Nella stampa scegli «Salva come PDF».</span>
        <button type="button" className="primary" onClick={() => window.print()}>
          Stampa o salva PDF
        </button>
      </div>
      {content}
    </div>
  );
}

function Letterhead({
  reference,
  children,
}: {
  reference: DocumentReference;
  children?: ReactNode;
}) {
  return (
    <header className="doc-head">
      <img src="/k-rev-logo-arancio.png" alt="K-Rev" />
      <div className="doc-ref">
        <strong>{reference.title}</strong>
        {reference.detail}
        {children}
      </div>
    </header>
  );
}

function Pairs({ pairs, className }: { pairs: DocumentPair[]; className: string }) {
  return (
    <dl className={className}>
      {pairs.map((pair) => (
        <PairRow key={pair.label} pair={pair} />
      ))}
    </dl>
  );
}

function PairRow({ pair }: { pair: DocumentPair }) {
  return (
    <>
      <dt>{pair.label}</dt>
      <dd>{pair.value}</dd>
    </>
  );
}

/**
 * «Offerta economica», model «Semplice» (revised 04/10/2026, 2.58): the orange
 * only on the logo and the platform; each section with the colour of its area;
 * the notes under the lines; conditions on a neutral ground, then the
 * references in K-Rev; signatures at the bottom of the page.
 */
function OfferDocument({ model }: { model: OfferDocumentModel }) {
  return (
    <article className="doc-page offer">
      <Letterhead reference={model.reference} />
      <h1>{model.title}</h1>
      {model.subtitle && <p className="doc-subtitle">{model.subtitle}</p>}

      <section className="doc-parties">
        <div>
          <span className="doc-label">Per</span>
          <strong>{model.client.name}</strong>
          {model.client.address && <div>{model.client.address}</div>}
          {model.client.vatNumber && <div>P.IVA / C.F. {model.client.vatNumber}</div>}
        </div>
      </section>

      <table className="offer-plan">
        <thead>
          <tr>
            <th>Attività</th>
            <th>Ricorrenza</th>
            <th className="num">Importo</th>
          </tr>
        </thead>
        {model.groups.map((group, index) => (
          <tbody key={index} style={{ '--area': group.colour } as CSSProperties}>
            {group.name && (
              <tr className="offer-area">
                <td colSpan={2}>{group.name}</td>
                <td className="num">{group.subtotal}</td>
              </tr>
            )}
            {group.lines.map((line, lineIndex) => (
              <tr key={lineIndex}>
                <td className="offer-what">{line.what}</td>
                <td className="offer-when">{line.when}</td>
                <td className="num">{line.amount}</td>
              </tr>
            ))}
          </tbody>
        ))}
        {model.discount && (
          <tbody className="offer-discount">
            <tr>
              <td colSpan={2}>Sconto sull’offerta</td>
              <td className="num">{model.discount}</td>
            </tr>
          </tbody>
        )}
      </table>
      {model.clientNotes && <p className="offer-notes">{model.clientNotes}</p>}

      <div className="offer-total">
        <span>
          Totale dell’offerta
          <small>{model.totalNote}</small>
        </span>
        <strong>{model.total}</strong>
      </div>

      <Pairs pairs={model.terms} className="offer-terms" />
      {model.contacts.length > 0 && (
        <section className="offer-contacts">
          <span className="doc-label">I vostri riferimenti in K-Rev:</span>
          <ul>
            {model.contacts.map((contact) => (
              <li key={contact.role}>
                <span className="doc-label">{contact.role}:</span>
                <strong>{contact.name}</strong> - {contact.email}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="doc-signatures">
        {model.signatures.map((signature) => (
          <div key={signature.title}>
            {signature.title}
            <span>{signature.note}</span>
          </div>
        ))}
      </section>
      <footer className="doc-foot">
        {model.footer.map((part) => (
          <span key={part}>{part}</span>
        ))}
      </footer>
    </article>
  );
}

/** The internal document of the preventivo: revenues, costs, margins, checks, approvals. */
function PreventivoDocument({ model }: { model: PreventivoDocumentModel }) {
  return (
    <article className="doc-page">
      <Letterhead reference={model.reference}>
        <div>
          <span className="doc-ribbon">{model.ribbon}</span>
        </div>
      </Letterhead>
      <h1>{model.title}</h1>
      <p className="doc-subtitle">{model.subtitle}</p>
      <Pairs pairs={model.facts} className="doc-pairs doc-block" />

      <table className="doc-table">
        <thead>
          <tr>
            <th>Riga</th>
            <th className="num">Q.tà</th>
            <th className="num">Ricavo</th>
            <th className="num">Costo</th>
            <th className="num">Margine</th>
          </tr>
        </thead>
        <tbody>
          {model.groups.map((group, index) => (
            <GroupRows key={index} name={group.name}>
              {group.lines.map((line, lineIndex) => (
                <tr key={lineIndex}>
                  <td>
                    {line.what}
                    {line.costs && <small>{line.costs}</small>}
                  </td>
                  <td className="num">{line.quantity}</td>
                  <td className="num">{line.revenue}</td>
                  <td className="num">{line.cost}</td>
                  <td className="num">{line.margin}</td>
                </tr>
              ))}
            </GroupRows>
          ))}
          {model.transversal && (
            <tr>
              <td colSpan={3}>Costi trasversali (fee, trasferte)</td>
              <td className="num">{model.transversal}</td>
              <td />
            </tr>
          )}
          <tr className="doc-strong">
            <td colSpan={2}>Totale (IVA esclusa)</td>
            <td className="num">{model.total.revenue}</td>
            <td className="num">{model.total.cost}</td>
            <td className="num">{model.total.margin}</td>
          </tr>
        </tbody>
      </table>
      <p className="doc-note">{model.note}</p>

      <div className="doc-block">
        <h2>Condizioni proposte al cliente</h2>
        <Pairs pairs={model.conditions} className="doc-pairs" />
      </div>

      <div className="doc-block">
        <h2>Controlli</h2>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Controllo</th>
              <th>Valore</th>
              <th>Esito</th>
            </tr>
          </thead>
          <tbody>
            {model.checks.map((check) => (
              <tr key={check.label}>
                <td>{check.label}</td>
                <td>{check.detail}</td>
                <td className={check.bad ? 'doc-bad' : undefined}>{check.outcome}</td>
              </tr>
            ))}
            <tr className="doc-strong">
              <td colSpan={2}>Esito del preventivo</td>
              <td className={model.outcome.bad ? 'doc-bad' : undefined}>{model.outcome.text}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="doc-block">
        <h2>Approvazioni registrate</h2>
        <Pairs pairs={model.approvals} className="doc-pairs" />
      </div>
      <footer className="doc-foot">{model.footer}</footer>
    </article>
  );
}

function GroupRows({ name, children }: { name: string | null; children: ReactNode }) {
  return (
    <>
      {name && (
        <tr className="doc-section">
          <td colSpan={99}>{name}</td>
        </tr>
      )}
      {children}
    </>
  );
}
